import React, { createContext, useState, useContext, useEffect } from 'react';
import { useAuth } from './AuthContext';

const RoleContext = createContext();

export const RoleProvider = ({ children }) => {
  const { user } = useAuth();
  const [selectedRole, setSelectedRole] = useState(null);
  const [availableRoles, setAvailableRoles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user?.school_users) {
      // Obter todos os papéis ativos do usuário
      const activeRoles = user.school_users
        .filter(su => su.active)
        .map(su => ({
          id: su.id,
          role: su.role,
          role_display: su.role_display,
          school_id: su.school?.id || su.school,
          school_name: su.school?.name || 'Escola'
        }));

      // Agrupar por papel único (caso tenha mesmo papel em várias escolas)
      const uniqueRoles = [];
      const seenRoles = new Set();
      
      activeRoles.forEach(ar => {
        if (!seenRoles.has(ar.role)) {
          seenRoles.add(ar.role);
          uniqueRoles.push({
            role: ar.role,
            role_display: ar.role_display,
            schools: activeRoles.filter(r => r.role === ar.role)
          });
        }
      });

      setAvailableRoles(uniqueRoles);

      // Tentar recuperar papel salvo no localStorage
      const savedRole = localStorage.getItem('selectedRole');
      
      if (savedRole && uniqueRoles.find(r => r.role === savedRole)) {
        setSelectedRole(savedRole);
      } else if (uniqueRoles.length === 1) {
        // Se tiver apenas um papel, selecionar automaticamente
        setSelectedRole(uniqueRoles[0].role);
        localStorage.setItem('selectedRole', uniqueRoles[0].role);
      }

      setLoading(false);
    }
  }, [user]);

  const changeRole = (role) => {
    setSelectedRole(role);
    localStorage.setItem('selectedRole', role);
  };

  const hasMultipleRoles = () => {
    return availableRoles.length > 1;
  };

  const getSelectedRoleData = () => {
    return availableRoles.find(r => r.role === selectedRole);
  };

  const clearRole = () => {
    setSelectedRole(null);
    localStorage.removeItem('selectedRole');
  };

  return (
    <RoleContext.Provider 
      value={{ 
        selectedRole, 
        setSelectedRole: changeRole,
        availableRoles,
        loading,
        hasMultipleRoles,
        getSelectedRoleData,
        clearRole
      }}
    >
      {children}
    </RoleContext.Provider>
  );
};

export const useRole = () => {
  const context = useContext(RoleContext);
  if (!context) {
    throw new Error('useRole must be used within a RoleProvider');
  }
  return context;
};
